import React, { useEffect, useState } from 'react';
import {
  ADMIN_HEADER_BACKGROUND_COLOR,
  ADMIN_HEADER_BORDER_MUTED_COLOR,
  ADMIN_HEADER_RGB,
  adminPageHeaderShadowRestClass,
  adminPageHeaderShadowScrolledClass,
} from '@/config/adminTheme';

interface AdminPageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  back?: React.ReactNode;
}

const HEADER_HEIGHT = 120;
const HEADER_WITH_BACK_HEIGHT = 148;

const AdminPageHeader: React.FC<AdminPageHeaderProps> = ({
  title,
  subtitle,
  actions,
  back,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const root = document.scrollingElement ?? document.documentElement;
    const onScroll = () => {
      setIsScrolled(root.scrollTop > 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={[
        'relative px-10 flex flex-col justify-end pb-6 overflow-hidden border-b',
        'ring-1 ring-inset ring-white/[0.16]',
        'transition-[box-shadow,background-color] duration-300 ease-out',
        isScrolled
          ? adminPageHeaderShadowScrolledClass
          : adminPageHeaderShadowRestClass,
      ].join(' ')}
      style={{
        backgroundColor: ADMIN_HEADER_BACKGROUND_COLOR,
        borderBottomColor: ADMIN_HEADER_BORDER_MUTED_COLOR,
        height: back ? HEADER_WITH_BACK_HEIGHT : HEADER_HEIGHT,
        minHeight: back ? HEADER_WITH_BACK_HEIGHT : HEADER_HEIGHT,
      }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-100"
        aria-hidden
        style={{
          backgroundImage: [
            `linear-gradient(155deg,rgb(${ADMIN_HEADER_RGB} / 0.18) 0%,transparent 45%)`,
            'radial-gradient(100% 85% at 0% 0%,rgb(255 255 255 / 0.16),transparent 50%)',
            'radial-gradient(90% 75% at 100% 0%,rgb(168 130 230 / 0.14),transparent 52%)',
            'radial-gradient(70% 55% at 50% 120%,rgb(60 36 121 / 0.22),transparent 55%)',
          ].join(','),
        }}
      />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-white/45 to-transparent opacity-90"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-transparent via-[rgb(186_165_235)] to-transparent opacity-95"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-6 bottom-0 h-px bg-gradient-to-r from-[rgb(210_195_245/0.5)] via-[rgb(230_220_252/0.85)] to-[rgb(210_195_245/0.5)]"
        aria-hidden
      />
      {back && (
        <div className="relative z-[1] mb-4 flex items-center text-white/90 [&_button]:!text-white/90 [&_button:hover]:!text-white [&_button]:transition-colors">
          {back}
        </div>
      )}
      <div className="relative z-[1] flex items-end justify-between gap-8">
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold text-white tracking-[-0.02em] leading-tight truncate font-header drop-shadow-[0_2px_8px_rgb(0_0_0/0.28)]">
            {title}
          </h1>
          {subtitle && (
            <p className="text-[13px] mt-2 leading-relaxed font-content tracking-wide !text-white [text-shadow:0_1px_2px_rgb(0_0_0/0.2)]">
              {subtitle}
            </p>
          )}
        </div>
        {actions && (
          <div
            className={[
              'relative shrink-0 pb-0.5',
              // The shared `actions` slot renders whatever the page passes in
              // (today, always RefreshButton) against this header's dark
              // violet background — its own light-mode colors (near-white
              // border, dark navy text) are unreadable here. Force a muted
              // white treatment instead, matching the `back` slot's own
              // onDark override just above: visible, but no loud color.
              '[&_button]:!text-white/80',
              '[&_button:hover]:!text-white',
              '[&_button]:!border-white/25',
              '[&_button:hover]:!border-white/40',
              '[&_button:hover]:!bg-white/10',
              '[&_button]:!bg-transparent',
              '[&_button]:transition-colors',
            ].join(' ')}
          >
            {actions}
          </div>
        )}
      </div>
    </header>
  );
};

export const BackButton: React.FC<{
  onClick: () => void;
  label?: string;
  variant?: 'default' | 'onDark';
}> = ({ onClick, label = 'Back', variant = 'default' }) => (
  <button
    type="button"
    onClick={onClick}
    className={
      variant === 'onDark'
        ? 'inline-flex items-center gap-1.5 text-[13px] font-medium text-white/90 hover:text-white transition-colors duration-200'
        : 'inline-flex items-center gap-1.5 text-[13px] font-medium text-[#9B919D] hover:text-[#3C2479] transition-colors duration-200'
    }
  >
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M19 12H5M5 12L12 19M5 12L12 5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    {label}
  </button>
);

export default AdminPageHeader;
